import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Smartphone } from "lucide-react";
import { getBackendUrl } from "../lib/utils";

/**
 * JoinQR — student-display overlay that shows a QR code teachers can
 * scan with the Sparkl Teacher mobile app to pair their phone as a
 * co-controller. The QR encodes JSON of the form
 *   { app: "sparkl-teacher", code: "NNNNNN" }
 * so the scanner can validate it's a Sparkl pairing code before
 * connecting.
 *
 * Reads the join code that the Landing page stashed in localStorage
 * under `sparkl-join-code-${sessionId}`.
 */
const JoinQR = ({ sessionId, compact = false }) => {
    const [code, setCode] = useState(null);
    const [hidden, setHidden] = useState(false);
    const [serverUrl, setServerUrl] = useState(null);

    useEffect(() => {
        if (!sessionId) return;
        const key = `sparkl-join-code-${sessionId}`;
        let value = null;
        try {
            value =
                sessionStorage.getItem(key) || localStorage.getItem(key) || null;
        } catch {
            value = null;
        }
        if (value) {
            setCode(value);
            // We NO LONGER check stashedUrl here, we always fetch from backend
            // to ensure we get the fresh server_url if IP has changed.
        }
        // Fallback: ask the backend (Landing.jsx may not have set localStorage
        // if the student window opened first).
        const base = getBackendUrl();
        if (!base) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch(
                    `${base}/axis-ai/v1/octane/sessions/${encodeURIComponent(
                        sessionId,
                    )}/ensure`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ lesson_id: "photosynthesis" }),
                    },
                );
                if (!r.ok) return;
                const data = await r.json();
                if (!cancelled) {
                    if (data?.join_code) setCode(data.join_code);
                    if (data?.server_url) setServerUrl(data.server_url);
                    try {
                        if (data?.join_code) {
                            sessionStorage.setItem(key, data.join_code);
                            localStorage.setItem(key, data.join_code);
                        }
                        if (data?.server_url) {
                            sessionStorage.setItem(urlKey, data.server_url);
                            localStorage.setItem(urlKey, data.server_url);
                        }
                    } catch {
                        /* noop */
                    }
                }
            } catch {
                /* offline */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    if (!code || hidden) return null;

    // Dynamically resolve the backend URL to handle laptop IP changes!
    const resolvedServerUrl = serverUrl || (() => {
        let u = process.env.REACT_APP_BACKEND_URL;
        const currentHostname = window.location.hostname;
        // If accessed via local network IP (not localhost/127.0.0.1), dynamically adapt the backend URL!
        if (currentHostname && currentHostname !== "localhost" && currentHostname !== "127.0.0.1" && !currentHostname.includes("localhost")) {
            try {
                const urlObj = new URL(u);
                urlObj.hostname = currentHostname;
                u = urlObj.toString().replace(/\/$/, "");
            } catch {
                // Fallback
            }
        }
        return u;
    })();

    const payload = JSON.stringify({
        app: "sparkl-teacher",
        code,
        serverUrl: resolvedServerUrl
    });


    if (compact) {
        return (
            <div
                data-testid="student-join-qr-compact"
                className="fixed top-4 left-4 z-40 bg-white/95 backdrop-blur rounded-2xl border border-black/[0.06] shadow-[0_10px_28px_rgba(20,32,42,0.18)] p-3 flex items-center gap-3"
            >
                <QRCodeSVG
                    value={payload}
                    size={56}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#14202a"
                />
                <div className="flex flex-col">
                    <span className="text-[8px] tracking-[2.4px] uppercase font-bold text-[#4a5662]">
                        Phone code
                    </span>
                    <span className="text-base font-bold text-[#14202a] tracking-[0.18em] tabular-nums">
                        {code}
                    </span>
                </div>
                <button
                    onClick={() => setHidden(true)}
                    className="ml-1 w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/[0.04]"
                    title="Hide"
                    data-testid="student-qr-hide-btn"
                >
                    <X className="w-3.5 h-3.5 text-[#4a5662]" />
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="student-join-qr"
            className="absolute bottom-8 right-8 z-30 bg-white/95 backdrop-blur rounded-3xl border border-black/[0.06] shadow-[0_20px_50px_rgba(20,32,42,0.18)] p-5 flex flex-col items-center max-w-[260px]"
        >
            <div className="flex items-center gap-1.5 mb-2">
                <Smartphone className="w-3 h-3 text-[#2E8E9A]" />
                <span className="text-[9px] tracking-[2.4px] uppercase font-bold text-[#4a5662]">
                    Connect teacher app
                </span>
            </div>
            <div className="rounded-xl border border-black/[0.05] p-2 bg-white">
                <QRCodeSVG
                    value={payload}
                    size={140}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#14202a"
                />
            </div>
            <span className="mt-3 text-[10px] tracking-[2.4px] uppercase font-bold text-[#4a5662]">
                Or enter code
            </span>
            <span className="text-2xl font-bold text-[#14202a] tracking-[0.22em] tabular-nums">
                {code}
            </span>
            <span className="mt-2 text-[10px] text-[#4a5662] text-center leading-snug">
                Scan with the Sparkl Teacher app to co-control this session
                from your phone.
            </span>
            <button
                onClick={() => setHidden(true)}
                className="mt-3 text-[10px] tracking-[1.8px] uppercase font-bold text-[#4a5662] hover:text-[#14202a]"
                data-testid="student-qr-dismiss-btn"
            >
                Dismiss
            </button>
        </div>
    );
};

export default JoinQR;
